import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";

import { getCommonCopy } from "@base/content";

export default function Header() {
  const [location] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const copy = getCommonCopy().header;
  const navItems = copy.nav;

  const scrollToSection = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    setMobileMenuOpen(false);

    // If it's a regular page link (not an anchor), let it navigate normally
    if (!href.includes("#")) {
      return;
    }

    // For anchor links, only handle if we're on the home page
    if (location !== "/") return;

    const targetId = href.split("#")[1];
    if (!targetId) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const element = document.getElementById(targetId);
    if (element) {
      e.preventDefault();
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#6c2db4] backdrop-blur-lg border-b border-border">
      <nav className="container">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="/"
            onClick={e => scrollToSection(e, "/")}
            className="flex items-center gap-2 group"
          >
            <img
              src="/logo.webp"
              alt="Codecious Logo"
              width="167"
              height="40"
              className="h-10 w-auto transition-transform group-hover:scale-110"
            />
          </a>

          {/* Desktop Navigation (from lg: four labels do not fit a tablet row) */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-8">
            {navItems.map(item => (
              <a
                key={item.href}
                href={item.href}
                onClick={e => scrollToSection(e, item.href)}
                className="text-sm font-medium text-white bg-white/10 rounded-full px-4 py-2 whitespace-nowrap hover:bg-white/20 transition-colors"
              >
                {item.label}
              </a>
            ))}
          </div>

          {/* CTA Button - Desktop */}
          <div className="hidden lg:block">
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-primary to-[oklch(0.623_0.214_240)] hover:opacity-90 transition-opacity"
            >
              <a
                href={copy.cta.href}
                onClick={e => scrollToSection(e, copy.cta.href)}
              >
                {copy.cta.label}
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? copy.closeMenu : copy.openMenu}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div
            id="mobile-menu"
            className="lg:hidden py-4 border-t border-white/20 animate-in fade-in slide-in-from-top-2 duration-200"
          >
            <div className="flex flex-col gap-4">
              {navItems.map(item => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={e => scrollToSection(e, item.href)}
                  className="text-base font-medium text-white/70 hover:text-white transition-colors py-2"
                >
                  {item.label}
                </a>
              ))}
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-primary to-[oklch(0.623_0.214_240)] hover:opacity-90 transition-opacity w-full"
              >
                <a
                  href={copy.cta.href}
                  onClick={e => scrollToSection(e, copy.cta.href)}
                >
                  {copy.cta.label}
                </a>
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
